import { Box, useTheme } from '@mui/material';

/**
 * v4 badge (DESIGN_SYSTEM.md section 8.3, #1377). Type/role indicator.
 * Lowercase, three-character minimum, never with icons.
 *
 * Flat variants (12px, 2px 7px):
 *   warning     - owner / attention (tint.warning + warning)
 *   interactive - editor (tint.checking + cat.checking, per spec)
 *   neutral     - viewer / solo (tint.neutral + muted text)
 *   household   - household scope (tint.interactive + interactive)
 *
 * Bordered chip variants (11px, 3px 9px, hairline border):
 *   owner        - color-mixed warning fill + border
 *   current      - color-mixed positive fill + border (current workspace)
 *   unreconciled - filled warning chip (warning.chipBg/border/deep)
 *   dev          - unreconciled treatment + uppercase caps tracking (env flag)
 *
 * Theme-native: every color from theme.palette. No hardcoded hex.
 *
 * @param {object} props
 * @param {('warning'|'interactive'|'neutral'|'household'|'owner'|'current'|'unreconciled'|'dev')} [props.variant]
 * @param {React.ReactNode} props.children  badge text
 * @param {object} [props.sx]               extra sx merged onto the badge
 */
const Badge = ({ variant = 'neutral', children, sx, ...rest }) => {
  const theme = useTheme();
  const { ds, palette } = theme;

  const flat = {
    warning:     { bg: palette.tint.warning,     fg: palette.warning.main },
    interactive: { bg: palette.tint.checking,    fg: palette.cat.checking },
    neutral:     { bg: palette.tint.neutral,     fg: palette.text.secondary },
    household:   { bg: palette.tint.interactive, fg: palette.interactive },
  };
  const chip = {
    owner: {
      bg: `color-mix(in srgb, ${palette.warning.main} 8%, transparent)`,
      border: `color-mix(in srgb, ${palette.warning.main} 30%, transparent)`,
      fg: palette.warning.main,
    },
    current: {
      bg: `color-mix(in srgb, ${palette.success.main} 8%, transparent)`,
      border: `color-mix(in srgb, ${palette.success.main} 25%, transparent)`,
      fg: palette.success.main,
    },
    unreconciled: {
      bg: palette.warning.chipBg,
      border: palette.warning.border,
      fg: palette.warning.deep,
    },
    dev: {
      bg: palette.warning.chipBg,
      border: palette.warning.border,
      fg: palette.warning.deep,
      caps: true,
    },
  };

  const c = chip[variant] || flat[variant] || flat.neutral;
  const isChip = variant in chip;

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        fontSize: isChip ? '11px' : `${ds.font.dim}px`,
        fontWeight: ds.font.weightMedium,
        padding: isChip ? '3px 9px' : '2px 7px',
        borderRadius: `${ds.radius.badge}px`,
        backgroundColor: c.bg,
        color: c.fg,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
        ...(c.border && { border: `1px solid ${c.border}` }),
        ...(c.caps && { textTransform: 'uppercase', letterSpacing: ds.font.letterUpper }),
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
};

export default Badge;
